import { Chip as MuiChip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { XIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { ReactElement } from 'react'

export type BaseChipPropTypes<ChipValueType> = {
    label: string
    value: ChipValueType
    selected?: boolean
    disabled?: boolean
    icon?: ReactElement
}

type ChipPropTypes<ChipValueType> = {
    toggleChip?: (value: ChipValueType) => void
    deleteChip?: (value: ChipValueType) => void
} & BaseChipPropTypes<ChipValueType>

export function Chip<ChipValueType>({
    label,
    value,
    selected,
    toggleChip,
    deleteChip,
    disabled = false,
    icon,
}: ChipPropTypes<ChipValueType>) {
    return (
        <StyledChip
            aria-label={label}
            label={label}
            variant="outlined"
            clickable={false} // this is to disable ripple
            onClick={toggleChip ? () => toggleChip(value) : deleteChip && (() => deleteChip(value))}
            onDelete={deleteChip && (() => deleteChip(value))}
            deleteIcon={deleteChip && <XIcon weight="bold" />}
            $selected={selected}
            $clickable={!!toggleChip || !!deleteChip}
            disabled={disabled}
            icon={icon}
        />
    )
}

const StyledChip = styled(MuiChip, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean; $clickable?: boolean }>(({ $selected, $clickable }) => ({
    height: '40px',
    ...tokens.Link,
    cursor: $clickable ? 'pointer' : undefined,
    borderRadius: tokens.SizingCornerRound,
    gap: tokens.SizingSpacing075,
    padding: `0 ${tokens.SizingSpacing200}`,
    border: `${tokens.SizingBorderS} solid`,
    borderColor: $selected ? tokens.ColorBorderSelectedDefault : tokens.ColorBorderDefault,
    backgroundColor: $selected ? tokens.ColorSurfaceSelectedDefault : tokens.ColorSurfaceDefault,
    '& .MuiChip-label': {
        padding: 0,
        color: $selected ? tokens.ColorTextLinkSelected : tokens.ColorTextLinkDefault,
    },
    '& *:not(.MuiChip-label)': {
        margin: 0,
        width: tokens.SizingIconS,
        height: tokens.SizingIconS,
        color: $selected ? tokens.ColorIconLinkSelected : tokens.ColorIconLinkDefault,
    },
    '&.Mui-disabled': {
        borderColor: tokens.ColorSurfaceDisabled,
        backgroundColor: tokens.ColorSurfaceDisabled,
        opacity: 1,
        '& .MuiChip-label': {
            color: tokens.ColorTextDisabled,
        },
        '& *:not(.MuiChip-label)': {
            color: tokens.ColorIconDisabled,
        },
    },
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
}))
